import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import { DownloadIcon, UploadIcon } from '@radix-ui/react-icons';
import {
    CopyIcon,
    CropIcon,
    EyeIcon,
    FilterIcon,
    HistoryIcon,
    LucideGalleryThumbnails,
    SendIcon,
    Settings2,
    Tablet,
    TagIcon,
} from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { ActivationDevice } from './hooks/useActivationDevices';
import CopyToActivationModal from './CopyToActivationModal';
import BulkTagModal from './BulkTagModal';
import {
    BulkEditModal,
    EditBatchProgress,
    EditHistoryModal,
} from '@/src/Common/components/MediaBulkEdit';
import { EditBatch } from '@/src/Common/components/hooks/useMediaEdit';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
} from '@/src/shadComponents/ui/dropdown-menu';

const filterOptions = [
    { name: 'Photo', value: 'processed' },
    { name: 'Video', value: 'processed-video' },
    { name: 'Edited', value: 'user-edit' },
    { name: 'Prints', value: 'composite' },
    { name: 'Originals', value: 'original' },
];

export default function PhotoManagerFilters({
    photos,
    setPhotos,
    onFilterChange,
    activationId,
    setIsShowingZipPrompt,
    setIsShowingBulkUpload,
    showHidden,
    setShowHidden,
    sortOrder,
    setSortOrder,
    selectedShareIdentifiers,
    setSelectedShareIdentifiers,
    columnCount,
    setColumnCount,
    setIsShowingSharePrompt,
    deviceFilter,
    setDeviceFilter,
    tagFilter,
    setTagFilter,
    activationDevices,
    allPhotos,
    autoRefresh,
    setAutoRefresh,
    onMediaEdited,
    galleryId,
}: {
    photos: MediaInstance[];
    onFilterChange: (filter: string[]) => void;
    onSortChange: (sort: 'asc' | 'desc') => void;
    activationId: number;
    isShowingZipPrompt: boolean;
    setIsShowingZipPrompt: (isShowing: boolean) => void;
    isShowingBulkUpload: boolean;
    setIsShowingBulkUpload: (isShowing: boolean) => void;
    showHidden: boolean;
    setShowHidden: (showHidden: boolean) => void;
    sortOrder: 'asc' | 'desc';
    setSortOrder: (sort: 'asc' | 'desc') => void;
    selectedShareIdentifiers: string[];
    setSelectedShareIdentifiers: (shareIdentifiers: string[]) => void;
    setPhotos: (photos: MediaInstance[]) => void;
    columnCount: number;
    setColumnCount: (columnCount: number) => void;
    setIsShowingSharePrompt: (isShowing: boolean) => void;
    deviceFilter: string | null;
    setDeviceFilter: (deviceFilter: string | null) => void;
    tagFilter: string | null;
    setTagFilter: (tagFilter: string | null) => void;
    activationDevices: ActivationDevice[];
    allPhotos: MediaInstance[];
    autoRefresh: boolean;
    setAutoRefresh: (autoRefresh: boolean) => void;
    onMediaEdited?: () => void;
    galleryId?: number | null;
}) {
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isDeviceFilterOpen, setIsDeviceFilterOpen] = useState(false);
    const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
    const [isShowingBulkTag, setIsShowingBulkTag] = useState(false);
    const authenticatedApi = useAuthenticatedApi();
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [isShowingBulkEdit, setIsShowingBulkEdit] = useState(false);
    const [activeEditBatch, setActiveEditBatch] = useState<EditBatch | null>(
        null,
    );
    const [isShowingEditHistory, setIsShowingEditHistory] = useState(false);

    // Calculate photo counts per device for the filter dropdown
    const devicePhotoCounts = useMemo(() => {
        const counts: Record<string, number> = { uploaded: 0 };
        
        for (const photo of allPhotos) {
            if (!photo.deviceKeyId) {
                counts.uploaded++;
            } else {
                counts[photo.deviceKeyId] = (counts[photo.deviceKeyId] || 0) + 1;
            }
        }
        
        return counts;
    }, [allPhotos]);

    // Tags in use across the activation, with counts, for the tag filter.
    // Rows carry their media_tag list, so no extra fetch is needed.
    const tagCounts = useMemo(() => {
        const counts = new Map<string, number>();

        for (const photo of allPhotos) {
            for (const tag of photo.tags ?? []) {
                counts.set(tag, (counts.get(tag) ?? 0) + 1);
            }
        }

        return Array.from(counts.entries()).sort((a, b) =>
            a[0].localeCompare(b[0]),
        );
    }, [allPhotos]);

    // Get display name for a device
    const getDeviceDisplayName = (deviceKeyId: string): string => {
        const device = activationDevices.find(d => d.deviceKeyId === deviceKeyId);
        return device?.displayName || device?.deviceName || 'Unknown Device';
    };

    // Get the current filter label
    const getDeviceFilterLabel = (): string => {
        if (deviceFilter === null) return 'All Sources';
        if (deviceFilter === 'uploaded') return 'Uploaded';
        return getDeviceDisplayName(deviceFilter);
    };

    useEffect(() => {
        if (typeFilter.length === 0) {
            setTypeFilter([
                'processed',
                'processed-video',
                'user-edit',
                'composite',
                'original',
            ]);
        }
    }, [setTypeFilter, typeFilter]);

    useEffect(() => {
        // When all types are selected, pass empty array to indicate no filter
        onFilterChange(typeFilter.length === 5 ? [] : typeFilter);

        // eslint-disable-next-line
    }, [typeFilter]);

    const [isHidden, setIsHidden] = useState(false);
    const [isShowingCopyToActivation, setIsShowingCopyToActivation] =
        useState(false);

    return (
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Left side: Title and Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Photos ({photos.length})
                    </h1>
                    
                    {/* Type Filter */}
                    <div className="relative">
                        <button
                            type="button"
                            className="bg-white border border-gray-300 flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-gray-50"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <FilterIcon className="h-4 w-4 mr-2 text-gray-500" />
                            {typeFilter.length === 5
                                ? 'All Types'
                                : `${typeFilter.length} Types`}
                        </button>

                        {isOpen && (
                            <div className="absolute mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                <div className="p-1">
                                    <div
                                        className="cursor-pointer hover:bg-gray-100 px-3 py-2 rounded text-sm"
                                        onClick={() => {
                                            setTypeFilter([
                                                'processed',
                                                'processed-video',
                                                'user-edit',
                                                'composite',
                                                'original',
                                            ]);
                                            setIsOpen(false);
                                        }}
                                    >
                                        All Types
                                    </div>
                                    <div className="border-t border-gray-100 my-1" />
                                    {filterOptions.map((option) => (
                                        <div
                                            key={option.value}
                                            className="cursor-pointer hover:bg-gray-100 px-3 py-1.5 rounded flex items-center text-sm"
                                            onClick={() => {
                                                setTypeFilter((prev) =>
                                                    prev.includes(option.value)
                                                        ? prev.filter((v) => v !== option.value)
                                                        : [...prev, option.value],
                                                );
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={typeFilter.includes(option.value)}
                                                onChange={() => {}}
                                                className="mr-2 h-4 w-4 rounded border-gray-300"
                                            />
                                            {option.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Source Filter */}
                    <div className="relative">
                        <button
                            type="button"
                            className="bg-white border border-gray-300 flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-gray-50"
                            onClick={() => setIsDeviceFilterOpen(!isDeviceFilterOpen)}
                        >
                            <Tablet className="h-4 w-4 mr-2 text-gray-500" />
                            {getDeviceFilterLabel()}
                        </button>

                        {isDeviceFilterOpen && (
                            <div className="absolute mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                <div className="p-1">
                                    <div
                                        className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                            deviceFilter === null ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            setDeviceFilter(null);
                                            setIsDeviceFilterOpen(false);
                                        }}
                                    >
                                        <span>All Sources</span>
                                        <span className="text-xs text-gray-400">{allPhotos.length}</span>
                                    </div>
                                    <div className="border-t border-gray-100 my-1" />
                                    {devicePhotoCounts.uploaded > 0 && (
                                        <div
                                            className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                deviceFilter === 'uploaded' ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={() => {
                                                setDeviceFilter('uploaded');
                                                setIsDeviceFilterOpen(false);
                                            }}
                                        >
                                            <span className="text-gray-500 italic">Uploaded</span>
                                            <span className="text-xs text-gray-400">{devicePhotoCounts.uploaded}</span>
                                        </div>
                                    )}
                                    {activationDevices.map((device) => {
                                        const count = devicePhotoCounts[device.deviceKeyId] || 0;
                                        if (count === 0) return null;
                                        return (
                                            <div
                                                key={device.deviceKeyId}
                                                className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                    deviceFilter === device.deviceKeyId ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => {
                                                    setDeviceFilter(device.deviceKeyId);
                                                    setIsDeviceFilterOpen(false);
                                                }}
                                            >
                                                <div className="flex flex-col">
                                                    <span className={device.friendlyName ? 'font-medium' : ''}>
                                                        {device.displayName}
                                                    </span>
                                                    {device.friendlyName && device.deviceName && (
                                                        <span className="text-xs text-gray-400">{device.deviceName}</span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-gray-400">{count}</span>
                                            </div>
                                        );
                                    })}
                                    {Object.entries(devicePhotoCounts)
                                        .filter(([key]) => key !== 'uploaded' && !activationDevices.some(d => d.deviceKeyId === key))
                                        .map(([deviceKeyId, count]) => (
                                            <div
                                                key={deviceKeyId}
                                                className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                    deviceFilter === deviceKeyId ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => {
                                                    setDeviceFilter(deviceKeyId);
                                                    setIsDeviceFilterOpen(false);
                                                }}
                                            >
                                                <span className="text-gray-500">Unknown Device</span>
                                                <span className="text-xs text-gray-400">{count}</span>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tag Filter — only offered once tags exist */}
                    {(tagCounts.length > 0 || tagFilter !== null) && (
                        <div className="relative">
                            <button
                                type="button"
                                className="bg-white border border-gray-300 flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-gray-50"
                                onClick={() =>
                                    setIsTagFilterOpen(!isTagFilterOpen)
                                }
                            >
                                <TagIcon className="h-4 w-4 mr-2 text-gray-500" />
                                {tagFilter === null
                                    ? 'All Tags'
                                    : tagFilter}
                            </button>

                            {isTagFilterOpen && (
                                <div className="absolute mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                    <div className="p-1 max-h-72 overflow-y-auto">
                                        <div
                                            className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                tagFilter === null
                                                    ? 'bg-blue-50'
                                                    : ''
                                            }`}
                                            onClick={() => {
                                                setTagFilter(null);
                                                setIsTagFilterOpen(false);
                                            }}
                                        >
                                            <span>All Tags</span>
                                            <span className="text-xs text-gray-400">
                                                {allPhotos.length}
                                            </span>
                                        </div>
                                        <div className="border-t border-gray-100 my-1" />
                                        {tagCounts.map(([tag, count]) => (
                                            <div
                                                key={tag}
                                                className={`cursor-pointer hover:bg-gray-100 px-3 py-2 rounded flex items-center justify-between text-sm ${
                                                    tagFilter === tag
                                                        ? 'bg-blue-50'
                                                        : ''
                                                }`}
                                                onClick={() => {
                                                    setTagFilter(tag);
                                                    setIsTagFilterOpen(false);
                                                }}
                                            >
                                                <span className="truncate">
                                                    {tag}
                                                </span>
                                                <span className="text-xs text-gray-400 ml-2 shrink-0">
                                                    {count}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* View Settings Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="bg-white border border-gray-300 flex items-center rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:bg-gray-50">
                                <Settings2 className="h-4 w-4 mr-2 text-gray-500" />
                                View
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuLabel>Sort Order</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
                                <DropdownMenuRadioItem value="desc">Newest First</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="asc">Oldest First</DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                            
                            <DropdownMenuSeparator />
                            
                            <DropdownMenuLabel>Columns</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={columnCount.toString()} onValueChange={(v) => setColumnCount(parseInt(v))}>
                                {[2, 3, 4, 5, 6].map((num) => (
                                    <DropdownMenuRadioItem key={num} value={num.toString()}>
                                        {num} Columns
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            
                            <DropdownMenuSeparator />
                            
                            <DropdownMenuCheckboxItem
                                checked={showHidden}
                                onCheckedChange={setShowHidden}
                            >
                                Show Hidden Photos
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuCheckboxItem
                                checked={autoRefresh}
                                onCheckedChange={setAutoRefresh}
                            >
                                Auto Refresh
                            </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Right side: Actions */}
                <div className="flex items-center gap-1">
                    <button
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => setIsShowingEditHistory(true)}
                        title="Bulk Edit History"
                    >
                        <HistoryIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => activationId && setIsShowingSharePrompt(true)}
                        title="Share"
                    >
                        <SendIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={async () => {
                            if (!activationId) return;
                            setIsReprocessing(true);
                            const result = await authenticatedApi.post(`/activations/${activationId}/reprocessThumbnails`);
                            toast.success(`Reprocessed ${result.data.updated} photos of ${result.data.count}.`);
                            setIsReprocessing(false);
                        }}
                        title="Reprocess Thumbnails"
                    >
                        {isReprocessing ? (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        ) : (
                            <LucideGalleryThumbnails className="h-5 w-5" />
                        )}
                    </button>
                    <button
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => activationId && setIsShowingZipPrompt(true)}
                        title="Download"
                    >
                        <DownloadIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => setIsShowingBulkUpload(true)}
                        title="Upload"
                    >
                        <UploadIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Selection toolbar - fixed at bottom */}
            {selectedShareIdentifiers.length > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-white rounded-lg px-4 py-2 border border-gray-200 shadow-lg z-50">
                    <span className="text-sm font-medium text-gray-700">
                        {selectedShareIdentifiers.length} selected
                    </span>
                    <div className="h-4 w-px bg-gray-300" />
                    <button
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        onClick={async () => {
                            const newHiddenState = !isHidden;
                            await authenticatedApi.put(`/media/share-identifiers/visibility`, {
                                shareIdentifiers: selectedShareIdentifiers,
                                isHidden: newHiddenState,
                            });
                            const newPhotos = photos.map((photo) => {
                                if (selectedShareIdentifiers.includes(photo.shareIdentifier)) {
                                    photo.isHidden = newHiddenState;
                                }
                                return photo;
                            });
                            setPhotos(newPhotos);
                            setIsHidden(newHiddenState);
                            toast.success(newHiddenState ? 'Photos hidden' : 'Photos visible');
                        }}
                        title={isHidden ? 'Show Photos' : 'Hide Photos'}
                    >
                        {isHidden ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
                    </button>
                    <button
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        onClick={() => setIsShowingBulkEdit(true)}
                        title="Crop Photos"
                    >
                        <CropIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        onClick={() => setIsShowingBulkTag(true)}
                        title="Apply or Remove Tags"
                    >
                        <TagIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        onClick={() => setIsShowingCopyToActivation(true)}
                        title="Copy to Activation"
                    >
                        <CopyIcon className="h-5 w-5" />
                    </button>
                    <button
                        className="text-sm text-gray-500 hover:text-gray-700"
                        onClick={() => setSelectedShareIdentifiers([])}
                    >
                        Clear
                    </button>
                </div>
            )}

            {isShowingBulkTag ? (
                <BulkTagModal
                    shareIdentifiers={selectedShareIdentifiers}
                    selectedPhotos={allPhotos.filter((p) =>
                        selectedShareIdentifiers.includes(p.shareIdentifier),
                    )}
                    allPhotos={allPhotos}
                    onDismiss={() => setIsShowingBulkTag(false)}
                    onComplete={() => {
                        setIsShowingBulkTag(false);
                        // Reload so thumbnails and the tag filter pick up
                        // the new tag state.
                        onMediaEdited?.();
                    }}
                />
            ) : null}

            {isShowingCopyToActivation ? (
                <CopyToActivationModal
                    shareIdentifiers={selectedShareIdentifiers}
                    activationId={activationId}
                    onDismiss={() => setIsShowingCopyToActivation(false)}
                    onComplete={() => {
                        setIsShowingCopyToActivation(false);
                        setSelectedShareIdentifiers([]);
                    }}
                />
            ) : null}

            {/* bulk edit: crop config → progress/report → history/rollback */}
            {isShowingBulkEdit ? (
                <BulkEditModal
                    activationId={activationId}
                    galleryId={galleryId ?? null}
                    media={allPhotos.filter((p) =>
                        selectedShareIdentifiers.includes(p.shareIdentifier),
                    )}
                    onClose={() => setIsShowingBulkEdit(false)}
                    onStarted={(batch) => {
                        setIsShowingBulkEdit(false);
                        setSelectedShareIdentifiers([]);
                        setActiveEditBatch(batch);
                    }}
                />
            ) : null}
            {activeEditBatch ? (
                <EditBatchProgress
                    batch={activeEditBatch}
                    onClose={() => {
                        setActiveEditBatch(null);
                        onMediaEdited?.();
                    }}
                    onChanged={() => onMediaEdited?.()}
                />
            ) : null}
            {isShowingEditHistory ? (
                <EditHistoryModal
                    activationId={activationId}
                    onClose={() => setIsShowingEditHistory(false)}
                    onChanged={() => onMediaEdited?.()}
                />
            ) : null}
        </div>
    );
}
